import React, { useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import type { ColorPalette } from '../../constants/colors';
import { darkColors } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useEmployees } from '../../hooks/useEmployees';
import type { EmployeeListItem } from '../../types/employee';

type Props = BottomTabScreenProps<MainTabParamList, 'Employees'>;

function statusLabel(item: EmployeeListItem) {
  if (item.status) return item.status;
  if (item.isActive === true) return 'Active';
  if (item.isActive === false) return 'Inactive';
  return 'Unknown';
}

function statusColor(label: string, palette: ColorPalette) {
  const normalized = label.trim().toLowerCase();
  if (normalized === 'active') return palette.success;
  if (normalized === 'inactive') return palette.error;
  return palette.textMuted;
}

function departmentLabel(item: EmployeeListItem) {
  return item.department || item.designation || 'Department not listed';
}

function SearchIcon({ color }: { color: string }) {
  return (
    <View style={iconStyles.searchRoot} accessibilityElementsHidden>
      <View style={[iconStyles.searchCircle, { borderColor: color }]} />
      <View style={[iconStyles.searchHandle, { backgroundColor: color }]} />
    </View>
  );
}

function FilterIcon({ color }: { color: string }) {
  return (
    <View style={iconStyles.filterRoot} accessibilityElementsHidden>
      <View style={[iconStyles.filterLine, iconStyles.filterWide, { backgroundColor: color }]} />
      <View style={[iconStyles.filterLine, iconStyles.filterMid, { backgroundColor: color }]} />
      <View style={[iconStyles.filterLine, iconStyles.filterNarrow, { backgroundColor: color }]} />
    </View>
  );
}

function PersonIcon({ color }: { color: string }) {
  return (
    <View style={iconStyles.personRoot} accessibilityElementsHidden>
      <View style={[iconStyles.personHead, { borderColor: color }]} />
      <View style={[iconStyles.personBody, { borderColor: color }]} />
    </View>
  );
}

function looksLikeEmployeeNumber(
  value: string | null | undefined,
  employeeNumber: string,
): boolean {
  if (!value?.trim()) return false;
  return value.trim().toLowerCase() === employeeNumber.trim().toLowerCase();
}

function EmployeeRow({
  item,
  palette,
  onPress,
}: {
  item: EmployeeListItem;
  palette: ColorPalette;
  onPress: () => void;
}) {
  const isDark = palette.background === darkColors.background;
  const status = statusLabel(item);
  const avatarBg = isDark ? '#24344F' : '#E8EEF7';
  const avatarFg = palette.primary;
  const username = looksLikeEmployeeNumber(item.username, item.employeeNumber)
    ? null
    : item.username;
  const displayName = looksLikeEmployeeNumber(item.displayName, item.employeeNumber)
    ? ''
    : item.displayName;
  const title = username || displayName;
  const showDisplayName = Boolean(
    displayName && (!username || displayName !== username),
  );
  const a11yParts = [username, displayName, item.department].filter(Boolean);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={a11yParts.join(', ') || 'Employee'}
      onPress={onPress}
      style={({ pressed }) => [
        styles.card,
        {
          backgroundColor: palette.surface,
          borderColor: palette.border,
          opacity: pressed ? 0.92 : 1,
          transform: [{ scale: pressed ? 0.985 : 1 }],
        },
      ]}
    >
      <View style={[styles.avatar, { backgroundColor: avatarBg }]}>
        <PersonIcon color={avatarFg} />
      </View>
      <View style={styles.cardBody}>
        {title ? (
          <Text style={[styles.name, { color: palette.textPrimary }]} numberOfLines={1}>
            {title}
          </Text>
        ) : null}
        {showDisplayName ? (
          <Text style={[styles.meta, { color: palette.textSecondary }]} numberOfLines={1}>
            {displayName}
          </Text>
        ) : null}
        <Text style={[styles.meta, { color: palette.textMuted }]} numberOfLines={1}>
          {departmentLabel(item)}
        </Text>
      </View>
      <Text style={[styles.status, { color: statusColor(status, palette) }]}>
        {status}
      </Text>
    </Pressable>
  );
}

export function EmployeeListScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const employees = useEmployees();
  const searchRef = useRef<TextInput>(null);
  const root = navigation.getParent<NativeStackNavigationProp<RootStackParamList>>();
  const canCreate = auth.hasPermission(PERMISSIONS.EMPLOYEE_CREATE);

  const footer = useMemo(() => {
    if (!employees.isLoadingMore) return null;
    return (
      <View style={styles.footer} accessibilityLabel="Loading more employees">
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [employees.isLoadingMore, palette.primary]);

  return (
    <PermissionGuard
      permission={PERMISSIONS.EMPLOYEE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title="Employees" />

        <View style={styles.body}>
        <View style={styles.searchRow}>
          <View
            style={[
              styles.searchField,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
              },
            ]}
          >
            <SearchIcon color={palette.textMuted} />
            <TextInput
              ref={searchRef}
              accessibilityLabel="Search"
              value={employees.search}
              onChangeText={employees.setSearch}
              placeholder="Search employee..."
              placeholderTextColor={palette.textMuted}
              autoCapitalize="none"
              autoCorrect={false}
              style={[styles.searchInput, { color: palette.textPrimary }]}
            />
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Filter employees"
            onPress={() => searchRef.current?.focus()}
            style={({ pressed }) => [
              styles.filterButton,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
                opacity: pressed ? 0.85 : 1,
              },
            ]}
          >
            <FilterIcon color={palette.textSecondary} />
          </Pressable>
        </View>

        {employees.isLoading && employees.items.length === 0 ? (
          <View style={styles.center}>
            <ActivityIndicator
              color={palette.primary}
              accessibilityLabel="Loading employees"
            />
          </View>
        ) : employees.error && employees.items.length === 0 ? (
          <ErrorState message={employees.error} onRetry={employees.retry} />
        ) : (
          <FlatList
            data={employees.items}
            keyExtractor={item => String(item.employeeId)}
            refreshControl={
              <RefreshControl
                refreshing={employees.isRefreshing}
                onRefresh={employees.refreshEmployees}
                tintColor={palette.primary}
              />
            }
            onEndReached={employees.loadMore}
            onEndReachedThreshold={0.4}
            ListFooterComponent={footer}
            ListEmptyComponent={
              <EmptyState
                title="No employees found"
                message="Try another search, or ask an administrator if records are missing."
                actionTitle="Retry"
                onAction={employees.retry}
              />
            }
            contentContainerStyle={[
              styles.listContent,
              canCreate ? styles.listContentWithFab : null,
            ]}
            showsVerticalScrollIndicator={false}
            renderItem={({ item }) => (
              <EmployeeRow
                item={item}
                palette={palette}
                onPress={() =>
                  root?.navigate(routes.employeeDetails, {
                    employeeId: item.employeeId,
                  })
                }
              />
            )}
          />
        )}
        </View>

        {canCreate ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Create employee"
            onPress={() => root?.navigate(routes.employeeCreate)}
            style={({ pressed }) => [
              styles.fab,
              {
                backgroundColor: palette.primary,
                opacity: pressed ? 0.9 : 1,
                transform: [{ scale: pressed ? 0.96 : 1 }],
              },
            ]}
          >
            <Text style={[styles.fabIcon, { color: palette.onPrimary }]}>+</Text>
          </Pressable>
        ) : null}
      </AppScreen>
    </PermissionGuard>
  );
}

const iconStyles = StyleSheet.create({
  searchRoot: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchCircle: {
    width: 11,
    height: 11,
    borderRadius: 6,
    borderWidth: 1.6,
  },
  searchHandle: {
    position: 'absolute',
    width: 6,
    height: 1.6,
    borderRadius: 1,
    bottom: 2,
    right: 1,
    transform: [{ rotate: '45deg' }],
  },
  filterRoot: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
  },
  filterLine: {
    height: 2,
    borderRadius: 1,
  },
  filterWide: { width: 14 },
  filterMid: { width: 10 },
  filterNarrow: { width: 6 },
  personRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  personHead: {
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 1.6,
    marginBottom: 2,
  },
  personBody: {
    width: 14,
    height: 8,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderWidth: 1.6,
    borderBottomWidth: 0,
  },
});

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    paddingBottom: 0,
  },
  body: {
    flex: 1,
    marginTop: spacing.lg,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  searchField: {
    flex: 1,
    minHeight: 48,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchInput: {
    ...typography.input,
    flex: 1,
    paddingVertical: spacing.sm,
  },
  filterButton: {
    width: 48,
    height: 48,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listContent: {
    flexGrow: 1,
    gap: spacing.md,
    paddingBottom: spacing.xl,
  },
  listContentWithFab: {
    paddingBottom: spacing.xxl + 56,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 16,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    gap: spacing.md,
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.06,
    shadowRadius: 14,
    elevation: 2,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardBody: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  name: {
    ...typography.body,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 22,
  },
  meta: {
    ...typography.caption,
    fontSize: 13,
    lineHeight: 18,
  },
  status: {
    ...typography.caption,
    fontWeight: '700',
    fontSize: 13,
    marginLeft: spacing.xs,
  },
  fab: {
    position: 'absolute',
    right: spacing.xl,
    bottom: spacing.xl,
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 6,
  },
  fabIcon: {
    fontSize: 32,
    lineHeight: 34,
    fontWeight: '400',
    marginTop: -2,
  },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
